import * as Sentry from "@sentry/react";
import { env } from "@/env";

/**
 * Initialize Sentry error tracking and performance monitoring.
 * Must be called before React renders, typically in main.tsx.
 *
 * @param router - TanStack Router instance for navigation tracking
 */
// biome-ignore lint/suspicious/noExplicitAny: Router types are complex and vary by configuration
export function initSentry(router: any) {
	// Skip initialization if no DSN configured (local development)
	if (!env.VITE_SENTRY_DSN) {
		console.info("[Sentry] Disabled - no DSN configured");
		return;
	}

	Sentry.init({
		dsn: env.VITE_SENTRY_DSN,
		environment: env.VITE_APP_ENVIRONMENT,
		release: `pilatestime-frontend@${env.VITE_APP_VERSION}`,

		// Send PII for better user identification (IP, headers)
		sendDefaultPii: true,
		enableLogs: true,
		integrations: [
			Sentry.consoleLoggingIntegration({ levels: ["error"] }),
			// TanStack Router integration for automatic pageload/navigation tracing
			Sentry.tanstackRouterBrowserTracingIntegration(router),

			// Session Replay for visual debugging of user sessions
			Sentry.replayIntegration({
				// Mask text content for privacy (can be disabled if needed)
				maskAllText: false,
				// Block media elements from replay
				blockAllMedia: false,
			}),
		],

		// Performance monitoring - sample rate for transactions
		tracesSampleRate: env.VITE_SENTRY_TRACES_SAMPLE_RATE,

		// Propagate traces to our backend API for distributed tracing
		tracePropagationTargets: [
			"localhost",
			/^https:\/\/backend-ecs\.(stage\.)?pilatestime\.com\.br/,
		],

		// Session Replay sampling rates
		replaysSessionSampleRate: env.VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE,
		replaysOnErrorSampleRate: env.VITE_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE,

		// Filter or sanitize events before sending
		beforeSend(event) {
			// You can modify or filter events here
			// Return null to drop the event
			return event;
		},
	});

	console.info(
		`[Sentry] Initialized for ${env.VITE_APP_ENVIRONMENT} environment`,
	);
}

/**
 * Set user context in Sentry for better error attribution.
 * Call this after user authentication.
 */
export function setSentryUser(
	user: {
		id: string;
		email?: string;
		username?: string;
	} | null,
) {
	if (user) {
		Sentry.setUser({
			id: user.id,
			email: user.email,
			username: user.username,
		});
	} else {
		Sentry.setUser(null);
	}
}

/**
 * Set organization context for multi-tenant error tracking.
 */
export function setSentryOrganization(orgId: string, orgName?: string) {
	Sentry.setTag("organization_id", orgId);
	if (orgName) {
		Sentry.setTag("organization_name", orgName);
	}
}

/**
 * Capture an exception manually with additional context.
 */
export function captureError(
	error: Error,
	context?: {
		tags?: Record<string, string>;
		extra?: Record<string, unknown>;
	},
) {
	Sentry.captureException(error, {
		tags: context?.tags,
		extra: context?.extra,
	});
}

// Re-export Sentry for direct access when needed
export { Sentry };
